import { useEffect, useRef, useState } from "react"
import type { RunEvent } from "../api/types"
import { apiFetch } from "./api"

interface SseState {
  events: RunEvent[]
  connected: boolean
  error: string | null
  // Phase 5 A.4: SSE warning/error event を live で蓄積。RunDetail が即時 UI に反映する。
  // result.json 完了前でも warnings/errors が見える。完了後は result.warnings/errors と
  // dedup される (同じ message が重複しないよう RunDetail 側で Set ベース merge)。
  liveWarnings: string[]
  liveErrors: string[]
  // Phase 5 A.3: 最新の cost.update payload (cumulative_cost_usd 等)。RunDetail で 80% toast 判定に使う。
  // Copilot 指摘 C-6: cost_budget_usd も SSE event 経由で取れるようにし、実行中も 80% 判定できる
  // (result.json は完了後にしか available でない)。
  latestCost: {
    cumulative_cost_usd: number
    cumulative_calls: number
    cost_budget_usd: number | null
  } | null
}

/**
 * Subscribe to /api/runs/{runId}/events.
 *
 * Backend は全イベントを SSE の既定 `message` event として送る (Phase 4.2 修正)。
 * `event_type` は data ペイロード内に閉じ込められているので、`addEventListener` を
 * 10 種類書かずに `onmessage` 単一で全イベントを受信できる。
 *
 * Cookie 認証は EventSource が same-origin で自動送信する (Vite dev proxy / prod
 * 同一 origin layout のどちらも該当)。
 *
 * 自動再接続は EventSource 標準機能 + Last-Event-ID で再開される。長期エラー時 (3 回
 * 連続 onerror) は /api/me を叩いて認証切れか確認 — 401 なら apiFetch が /login に
 * redirect する。バッファ上限を超えた古いイベントは捨てる。
 */
// Copilot r4 R4-1: 旧実装は `const EMPTY_STATE` を useState / setState で使い回していた。
// React は Object.is で前後 state を比較するため、同一参照が渡されると更新 skip が起きて
// 前 run の events/warnings が UI に残る race があった。factory にして毎回新参照を返す。
function createEmptyState(): SseState {
  return {
    events: [],
    connected: false,
    error: null,
    liveWarnings: [],
    liveErrors: [],
    latestCost: null,
  }
}

export function useRunEvents(runId: string | undefined, maxEvents = 500): SseState {
  const [state, setState] = useState<SseState>(createEmptyState)
  const eventsRef = useRef<RunEvent[]>([])
  const liveWarningsRef = useRef<string[]>([])
  const liveErrorsRef = useRef<string[]>([])
  const errorCountRef = useRef(0)

  useEffect(() => {
    if (!runId) return undefined

    eventsRef.current = []
    liveWarningsRef.current = []
    liveErrorsRef.current = []
    errorCountRef.current = 0
    // factory 経由で新参照を渡し、React に確実に再 render させる
    setState(createEmptyState())

    const url = `/api/runs/${encodeURIComponent(runId)}/events`
    const es = new EventSource(url, { withCredentials: true })

    es.onopen = () => {
      errorCountRef.current = 0
      setState((s) => ({ ...s, connected: true, error: null }))
    }

    es.onerror = () => {
      setState((s) => ({ ...s, connected: false, error: "connection error" }))
      errorCountRef.current += 1
      // 3 回連続失敗ごとに認証切れチェック。apiFetch は 401 で /login に自動 redirect。
      // M-6 (round 2 review): リセット契機が onopen のみだと SSE が一度 200 を返した後
      // に強制切断 → 401 になるケースで `=== 3` は二度とマッチしないので、
      // 「3 の倍数で繰り返しチェック」する。
      if (errorCountRef.current % 3 === 0) {
        apiFetch("/api/me").catch(() => {
          // 401 でなければ EventSource の auto-reconnect に任せる
        })
      }
    }

    es.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data) as RunEvent
        eventsRef.current = [...eventsRef.current, data].slice(-maxEvents)
        // Phase 5 A.4: warning / error event を live で蓄積 (完了前から UI 表示可)
        // Copilot 指摘 C-5: 無制限に grow すると long run / 失敗 loop で memory leak。
        // maxEvents 相当の cap を適用 (eventsRef と同じ上限)。
        const payload = (data.payload ?? {}) as Record<string, unknown>
        if (data.event_type === "warning") {
          const msg =
            typeof payload.message === "string" ? payload.message : JSON.stringify(payload)
          liveWarningsRef.current = [...liveWarningsRef.current, msg].slice(-maxEvents)
        } else if (data.event_type === "error") {
          const msg =
            typeof payload.message === "string" ? payload.message : JSON.stringify(payload)
          liveErrorsRef.current = [...liveErrorsRef.current, msg].slice(-maxEvents)
        }
        // Phase 5 A.3: cost.update payload を保持 (RunDetail で 80% toast 判定に使う)
        let nextCost: SseState["latestCost"] = null
        if (data.event_type === "cost.update") {
          const cumCost =
            typeof payload.cumulative_cost_usd === "number" ? payload.cumulative_cost_usd : 0
          const cumCalls =
            typeof payload.cumulative_calls === "number" ? payload.cumulative_calls : 0
          // Copilot 指摘 C-6: backend が送る cost_budget_usd を取り込む (実行中 80% toast 判定用)
          const budget =
            typeof payload.cost_budget_usd === "number" ? payload.cost_budget_usd : null
          nextCost = {
            cumulative_cost_usd: cumCost,
            cumulative_calls: cumCalls,
            cost_budget_usd: budget,
          }
        }
        setState((s) => ({
          ...s,
          events: eventsRef.current,
          connected: true,
          liveWarnings: liveWarningsRef.current,
          liveErrors: liveErrorsRef.current,
          latestCost: nextCost ?? s.latestCost,
        }))
      } catch {
        // ignore malformed events
      }
    }

    return () => {
      es.close()
    }
  }, [runId, maxEvents])

  return state
}
